import { t } from 'i18next';
import { useEffect } from 'react';

import history from 'src/history';
import { useAppDispatch } from 'src/hooks/useStore';
import { studioCreditApi } from 'src/services/api/studioCreditApi';
import { AppDispatch } from 'src/store/configureStore';
import { alertSnackbar } from 'src/store/ui/actions';

import { getStudioPlanTitle } from './studioFeatureGating';

const COMPLETED_ACTION_PARAM = 'completedAction';
const STUDIO_UPGRADE_ACTION = 'studioUpgrade';

// The checkout completion webhook performs the actual upgrade, so on return
// from Stripe the new plan can lag by a few webhook round-trips.
const UPGRADE_POLL_INTERVAL_MS = 2000;
const UPGRADE_POLL_MAX_ATTEMPTS = 10;

/**
 * The return URL a checkout-setup upgrade sends the customer back to — the page
 * they started on, marked so {@link useStudioUpgradeCheckoutReturn} recognises
 * the return.
 */
export const studioUpgradeReturnUrl = (pageUrl: string): string =>
  `${pageUrl}?${COMPLETED_ACTION_PARAM}=${STUDIO_UPGRADE_ACTION}`;

// Read through `history` rather than `window.location` so the marker is read
// from the same place it is stripped on.
const isStudioUpgradeReturn = (): boolean =>
  new URLSearchParams(history.location.search).get(COMPLETED_ACTION_PARAM) ===
  STUDIO_UPGRADE_ACTION;

// Handled once per page load rather than once per mounted hook: the return
// belongs to the page Stripe landed on, and several surfaces on it may mount
// this — they must not each poll and toast.
let hasHandledReturn = false;

// Polls the store directly instead of through the claiming component's query
// subscription, and is deliberately never cancelled: claiming the return
// consumes the URL marker, so nothing can retry it, and the surface that
// claimed it may well go away first (the sidebar card unmounts on navigation).
// Whoever starts this owes the user one of the two messages below.
const confirmUpgrade = async (dispatch: AppDispatch) => {
  for (let attempt = 0; attempt < UPGRADE_POLL_MAX_ATTEMPTS; attempt++) {
    const { data } = await dispatch(
      studioCreditApi.endpoints.getCurrentPlan.initiate(undefined, {
        subscribe: false,
        forceRefetch: true,
      }),
    );
    // checkout-setup only serves the Freemium→paid conversion, so any
    // resolved paid Studio plan means the upgrade landed.
    if (
      data?.family === 'studio' &&
      data.plan !== undefined &&
      data.plan !== 'freemium'
    ) {
      dispatch(
        alertSnackbar({
          successMessage: t(
            ($) => $.page_studio_plans.plan_change.upgrade_success,
            { plan: getStudioPlanTitle(data.plan) },
          ),
        }),
      );
      return;
    }
    await new Promise((resolve) => {
      setTimeout(resolve, UPGRADE_POLL_INTERVAL_MS);
    });
  }
  dispatch(
    alertSnackbar({
      successMessage: t(
        ($) => $.page_studio_plans.plan_change.upgrade_confirming,
      ),
    }),
  );
};

/**
 * Confirms an upgrade that completed through Stripe checkout. Checkout returns
 * with a full page load, so this runs on mount: it polls the current plan until
 * the completion webhook has applied the change, then reports it.
 *
 * Every surface that can start a checkout-setup upgrade should mount this on the
 * pages it returns to — the sidebar CTA and the plans page are both entry
 * points, and only whichever is mounted on the return page can confirm it.
 */
export const useStudioUpgradeCheckoutReturn = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    // Every other page load is a non-return, and costs no request at all.
    if (hasHandledReturn || !isStudioUpgradeReturn()) {
      return;
    }
    hasHandledReturn = true;
    // Strip the param so a refresh doesn't re-poll.
    const params = new URLSearchParams(history.location.search);
    params.delete(COMPLETED_ACTION_PARAM);
    history.replace({
      pathname: history.location.pathname,
      search: params.toString(),
    });

    void confirmUpgrade(dispatch);
  }, [dispatch]);
};
